import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import LiveCalculationExample from '../../ui/LiveCalculationExample';
import { generateSpecificSkillCalculation } from '@/lib/skills';

interface SkillCalculationModalProps {
  skillName: string;
  category: 'profession' | 'lead' | 'soft' | 'language' | 'tools';
  calculationType: 'professional' | 'activity' | 'company' | 'project' | 'private' | 'combined';
  skillsByCategory: any;
  activityId?: number;
  activities?: any[];
  children: React.ReactNode; // The clickable trigger element (e.g., Badge with percentage)
  className?: string;
  enhancedProjects?: any[]; // GitHub-enhanced projects for accurate skill calculations
}

export default function SkillCalculationModal({
  skillName,
  category,
  calculationType,
  skillsByCategory,
  activityId,
  activities,
  children,
  className = "",
  enhancedProjects = []
}: SkillCalculationModalProps) {
  
  // Generate the calculation data for this specific skill
  const calculationData = generateSpecificSkillCalculation(
    skillName,
    category,
    calculationType,
    skillsByCategory,
    activityId,
    activities,
    undefined, // projectTitle parameter
    enhancedProjects
  );

  // If no calculation data could be generated, don't make it clickable
  if (!calculationData) {
    return <>{children}</>;
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <div 
          className={`cursor-pointer hover:opacity-80 transition-opacity ${className}`}
          onClick={(e) => {
            console.log('SkillCalculationModal clicked!', { skillName, calculationType });
          }}
        >
          {children}
        </div>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] bg-gray-900 border-gray-700 text-gray-100 overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-gray-100">
            Skill Calculation Details: {skillName}
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto max-h-[70vh] pr-2 space-y-4">
          <div className="text-sm text-gray-300 mb-4">
            This shows exactly how the {calculationData.finalResult}% level for "{skillName}" was calculated 
            in the {calculationType === 'professional' ? 'professional overview' : 
                   calculationType === 'activity' ? 'activity context' : 
                   calculationType === 'company' ? 'company context' : 
                   calculationType === 'private' ? 'private projects overview' :
                   calculationType === 'combined' ? 'combined professional and private overview' :
                   'project context'}.
          </div>
          
          <LiveCalculationExample
            skillName={calculationData.skillName}
            calculationType={calculationType}
            totalProjects={calculationData.totalProjects}
            projectsWithSkill={calculationData.projectsWithSkill}
            totalDuration={calculationData.totalDuration}
            projects={calculationData.projects}
            totalSkillPoints={calculationData.totalSkillPoints}
            skillSpecificPoints={calculationData.skillSpecificPoints}
            finalResult={calculationData.finalResult}
            activityPeriod={calculationData.activityPeriod}
            allProjectDurations={calculationData.allProjectDurations}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
